import {
  createRouter,
  createWebHistory,
  NavigationGuardNext,
  RouteRecordRaw,
} from "vue-router";

import axios from "axios";
import jwt_decode from "jwt-decode";
import { User } from "shared/User";
import { getFeatureFlags } from "@/services/featureFlags";
import { AUTH_CALLBACK_PATH, sendToLogin } from "@/services/login";
// Using lazy imports to avoid TypeScript module resolution issues
const HomeView = () => import("@/views/HomeView.vue");
const UserView = () => import("@/views/UserView.vue");
import AdminView from "@/views/AdminView.vue";
import NotFoundView from "@/views/NotFoundView.vue";
const TicketWorkspaceView = (): Promise<any> => import("@/views/TicketWorkspaceView.vue");

const OverwatchProfileView = () => import("@/views/OverwatchProfileView.vue");
const OverwatchTrainView = () => import("@/views/OverwatchTrainView.vue");
const AuthCallbackView = () => import("@/views/AuthCallbackView.vue");
const ReportQueueView = () => import("@/views/ReportQueueView.vue");

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "Home",
    component: UserView as any,
  },
  {
    path: "/ticket/:ticketIdLoad",
    name: "Ticket View",
    component: UserView as any,
    props: (route) => ({
      selectedTicketId: route.params.ticketIdLoad.toString(),
    }),
  },
  {
    // Authorization-code login return (FEATURE_AUTH_CODE_FLOW); reachable without a token.
    path: AUTH_CALLBACK_PATH,
    name: "Auth Callback",
    component: AuthCallbackView as any,
    meta: { public: true },
  },
  {
    path: "/overwatch",
    name: "Overwatch",
    component: OverwatchProfileView as any,
  },
  {
    path: "/overwatch/train",
    name: "Overwatch Train",
    component: OverwatchTrainView as any,
  },
  // New route for three-column layout
  {
    path: "/workspace",
    name: "Ticket Workspace",
    component: TicketWorkspaceView as any,
  },
  {
    path: "/workspace/ticket/:ticketId",
    name: "Ticket Workspace View",
    component: TicketWorkspaceView as any,
    props: (route) => {
      const ticketId = route.params.ticketId.toString();
      return {
        initialTicketId: ticketId,
      };
    },
    meta: {
      requiresNewInstance: true, // Force new component instance on route change
    },
    beforeEnter: (to, from, next) => {
      next();
    },
  },
  {
    path: "/report-queue",
    name: "Report Queue",
    component: ReportQueueView as any,
  },
  {
    path: "/panel",
    name: "Admin panel",
    component: AdminView,
  },
  {
    path: '/admin/incidents',
    component: () => import('./views/AdminView.vue'),
  },
  {
    path: '/admin/incidents/:id',
    component: () => import('./views/AdminView.vue'),
  },
  {
    path: '/panel/incidents',
    redirect: '/admin/incidents',
  },
  {
    path: '/panel/incidents/:id',
    redirect: (to) => ({ path: `/admin/incidents/${to.params.id}` }),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

/**
 * Attempts to decode the user from the local storage.
 *
 * @returns The user object if it was successfully decoded, otherwise null.
 */
function decodeUserFromLocalStorage(): User | null {
  const tokenFromLS = window.localStorage.getItem("userToken");

  if (!tokenFromLS) {
    sendToLogin();
    return null;
  }

  const decoded = jwt_decode(tokenFromLS);
  if (!decoded) {
    location.reload();
    return null;
  }

  return decoded as User;
}

async function getUserPower(user: User): Promise<number> {
  try {
    const res = await axios.get("/user/power/" + user._id);
    // @ts-ignore
    const powerResponse = res as { power: number };
    return powerResponse.power;
  } catch (e) {
    return 0;
  }
}

router.beforeEach((to, from, next) => {
  if (to.meta.public) {
    next();
    return;
  }

  const user = decodeUserFromLocalStorage();

  if (user == null) {
    return;
  }

  getUserPower(user)
    .then(async (power) => {
      user.power = power;
      // @ts-ignore
      window.userObject = user;

      if (to.path.toLowerCase().startsWith("/panel") && user.power < 100) {
        next({ path: "/" });
        return;
      }

      if (to.path.toLowerCase().startsWith("/admin") && user.power < 100) {
        next({ path: "/" });
        return;
      }

      // Feature-flagged pages: unknown/off flags send the user home (flags fail closed)
      const lowerPath = to.path.toLowerCase();
      const needsJury = lowerPath.startsWith("/overwatch");
      const needsQueue = lowerPath.startsWith("/report-queue");
      if (needsJury || needsQueue) {
        const flags = await getFeatureFlags();
        if ((needsJury && !flags.juryReports) || (needsQueue && !flags.reportQueue)) {
          next({ path: "/" });
          return;
        }
      }

      // Staff report queue - only staff (power >= 2), same as the workspace
      if (to.path.toLowerCase().startsWith("/report-queue") && user.power < 2) {
        next({ path: "/" });
        return;
      }

      // Protect workspace routes - only staff (power >= 2) can access
      if (to.path.toLowerCase().startsWith("/workspace") && user.power < 2) {
        next({ path: "/" });
        return;
      }

      if (to.path === "/panel" && to.hash === "#incidents") {
        next({ path: "/admin/incidents" });
        return;
      }

      // Check view preference for staff users and redirect accordingly
      const viewPreference = localStorage.getItem('ticketViewPreference');
      const isStaff = user.power >= 2;
      
      if (isStaff && viewPreference) {
        const isUserRoute = to.path === '/' || to.path.startsWith('/ticket/');
        const isWorkspaceRoute = to.path.startsWith('/workspace');
        
        // Redirect user routes to workspace if preference is workspace
        if (viewPreference === 'workspace' && isUserRoute) {
          const ticketId = to.params.ticketIdLoad as string;
          if (ticketId) {
            next({ path: `/workspace/ticket/${ticketId}` });
          } else {
            next({ path: '/workspace' });
          }
          return;
        }
        
        // Redirect workspace routes to user view if preference is user
        if (viewPreference === 'user' && isWorkspaceRoute) {
          const ticketId = to.params.ticketId as string;
          if (ticketId) {
            next({ path: `/ticket/${ticketId}` });
          } else {
            next({ path: '/' });
          }
          return;
        }
      }

      next();
    })
    .catch((error) => {
      console.error('Router beforeEach: Error getting user power:', error);
      sendToLogin();
    });
});

export default router;
